import type { Action, ActionList, Business } from "@kaikei/contracts";
import { beginLogin, completeLogin, configuredClient, currentSession, logout } from "./auth";
import { isOAuthCallbackPath, mountOAuthCallbackBridge } from "./oauth-callback";
import "./styles.css";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787").replace(/\/$/, "");
const selectedRoot = document.querySelector<HTMLDivElement>("#app");
if (!selectedRoot) throw new Error("App root is missing.");
const appRoot = selectedRoot as HTMLDivElement;

let selectedBusiness: Business | null = null;
let businesses: Business[] = [];
let actions: Action[] = [];
let statusMessage = "";

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character] ?? character);
}

function render(): void {
  const authenticated = currentSession() !== null;
  appRoot.innerHTML = `
    <div class="shell">
      <header class="topbar">
        <a class="brand" href="/" aria-label="会計クラウド ホーム"><span class="brand-mark">K</span><span>会計クラウド</span></a>
        <div class="topbar-actions">
          ${authenticated ? `<span class="connection-state"><span class="status-dot"></span>接続中</span><button class="quiet-button" data-action="logout">ログアウト</button>` : ""}
        </div>
      </header>
      ${authenticated ? renderWorkspace() : renderWelcome()}
    </div>`;
  wireEvents();
}

function renderWelcome(): string {
  return `
    <main class="welcome" aria-labelledby="welcome-title">
      <div class="welcome-copy">
        <p class="eyebrow">AI NATIVE ACCOUNTING</p>
        <h1 id="welcome-title">今日の会計を、<br /><em>静かに整える。</em></h1>
        <p class="welcome-description">領収書や入出金を、事実を確認しながら正確な帳簿へ。AIが使えない時も、手動の流れで続けられます。</p>
        <button class="primary-button" data-action="login" ${configuredClient() ? "" : "disabled"}>OIDCでサインイン <span aria-hidden="true">→</span></button>
        ${configuredClient() ? "" : `<p class="hint">Web client IDを設定するとサインインできます。</p>`}
        ${statusMessage ? `<p class="inline-error" role="alert">${escapeHtml(statusMessage)}</p>` : ""}
      </div>
      <div class="welcome-card" aria-hidden="true"><div class="orb orb-large"></div><div class="orb orb-small"></div><div class="welcome-card-label">ACTION CLOUD</div><div class="welcome-card-title">今することが<br />見つかります</div></div>
    </main>`;
}

function renderWorkspace(): string {
  return `
    <main class="workspace" aria-labelledby="home-title">
      <aside class="sidebar" aria-label="メインナビゲーション">
        <button class="nav-item active" data-action="refresh"><span class="nav-icon">⌂</span>ホーム</button>
        <button class="nav-item" data-action="coming-soon"><span class="nav-icon">▤</span>取引</button>
        <button class="nav-item" data-action="coming-soon"><span class="nav-icon">◒</span>レポート</button>
        <div class="sidebar-spacer"></div>
        <button class="nav-item" data-action="coming-soon"><span class="nav-icon">⚙</span>設定</button>
      </aside>
      <section class="content">
        <div class="content-header"><div><p class="eyebrow">${selectedBusiness ? "MY BUSINESS" : "GET STARTED"}</p><h1 id="home-title">${selectedBusiness ? escapeHtml(selectedBusiness.name) : "はじめましょう"}</h1></div><button class="add-button" data-action="capture" aria-label="取引を追加">＋</button></div>
        ${renderBusinessPicker()}
        ${statusMessage ? `<p class="inline-error" role="status">${escapeHtml(statusMessage)}</p>` : ""}
        ${selectedBusiness ? renderActionCloud() : renderBusinessSetup()}
      </section>
      <aside class="chat-panel" aria-label="AIアシスタント"><div class="chat-kicker">ASSISTANT</div><h2>何を確認しますか？</h2><p>Action Cloudから、今することを選べます。</p><button class="chat-suggestion" data-action="refresh">未処理を更新 <span>↗</span></button><button class="chat-suggestion" data-action="coming-soon">今月の収支を見る <span>↗</span></button></aside>
    </main>`;
}

function renderBusinessPicker(): string {
  if (businesses.length < 2) return "";
  return `<label class="business-picker">事業を選択<select data-action="select-business">${businesses.map((business) => `<option value="${escapeHtml(business.id)}" ${business.id === selectedBusiness?.id ? "selected" : ""}>${escapeHtml(business.name)}</option>`).join("")}</select></label>`;
}

function renderBusinessSetup(): string {
  return `<div class="setup-card"><div class="setup-icon">✦</div><h2>最初の事業を作成</h2><p>事業名を登録すると、あなた専用の帳簿とAction Cloudが始まります。</p><form data-form="business"><label for="business-name">事業名</label><input id="business-name" name="name" placeholder="例：山田デザイン" required maxlength="200" /><button class="primary-button" type="submit">事業を作成 <span aria-hidden="true">→</span></button></form></div>`;
}

function renderActionCloud(): string {
  if (actions.length === 0) {
    return `<div class="empty-card"><div class="empty-check">✓</div><h2>今日は確認するものはありません</h2><p>新しい領収書や入出金を追加すると、ここに次のActionが表示されます。</p><button class="secondary-button" data-action="capture">＋ 取引を追加</button></div>`;
  }
  return `<section class="action-section" aria-labelledby="action-title"><div class="section-heading"><div><p class="eyebrow">NEXT ACTIONS</p><h2 id="action-title">今すること</h2></div><button class="text-button" data-action="refresh">更新</button></div><ol class="action-list">${actions.map(renderAction).join("")}</ol></section>`;
}

function renderAction(action: Action): string {
  const subtitle = action.presentation.subtitle ? `<span>${escapeHtml(action.presentation.subtitle)}</span>` : "";
  return `<li><button class="action-card attention-${action.presentation.attention}" data-action="start-action" data-id="${escapeHtml(action.id)}"><span class="priority-pill">${action.priority}</span><span class="action-copy"><strong>${escapeHtml(action.presentation.title)}</strong><small>${subtitle || "確認が必要です"}</small></span><span class="action-arrow" aria-hidden="true">↗</span></button></li>`;
}

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const auth = currentSession();
  if (!auth) throw new Error("サインインが必要です。");
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${auth.accessToken}`);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  if (selectedBusiness) headers.set("X-Business-ID", selectedBusiness.id);
  const response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  const value: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message = value && typeof value === "object" && "error" in value && value.error && typeof value.error === "object" && "message" in value.error && typeof value.error.message === "string" ? value.error.message : `API request failed (${response.status})`;
    throw new Error(message);
  }
  return value as T;
}

async function loadWorkspace(): Promise<void> {
  businesses = await apiRequest<Business[]>("/v1/businesses");
  selectedBusiness = businesses.find((business) => business.id === selectedBusiness?.id) ?? businesses[0] ?? null;
  if (selectedBusiness) {
    const list = await apiRequest<ActionList>("/v1/actions?status=open&limit=20");
    actions = list.items;
  } else actions = [];
}

async function onLogin(): Promise<void> {
  statusMessage = "";
  try {
    await beginLogin();
  } catch (error) {
    statusMessage = error instanceof Error ? error.message : "サインインを開始できませんでした。";
    render();
  }
}

async function onBusinessSubmit(form: HTMLFormElement): Promise<void> {
  const formData = new FormData(form);
  const name = formData.get("name");
  try {
    const created = await apiRequest<Business>("/v1/businesses", { method: "POST", body: JSON.stringify({ name }), headers: { "Idempotency-Key": crypto.randomUUID() } });
    selectedBusiness = created;
    await loadWorkspace();
    statusMessage = "事業を作成しました。";
    render();
  } catch (error) {
    statusMessage = error instanceof Error ? error.message : "事業を作成できませんでした。";
    render();
  }
}

async function onStartAction(id: string): Promise<void> {
  try {
    const result = await apiRequest<{ flow: { type: string; transaction_id: string } | null }>(`/v1/actions/${encodeURIComponent(id)}/start`, { method: "POST", body: "{}", headers: { "Idempotency-Key": crypto.randomUUID() } });
    statusMessage = result.flow ? "確認フローを開く準備ができました。" : "Actionを開始しました。";
    render();
  } catch (error) {
    statusMessage = error instanceof Error ? error.message : "Actionを開始できませんでした。";
    render();
  }
}

function wireEvents(): void {
  document.querySelectorAll<HTMLElement>("[data-action='login']").forEach((element) => element.addEventListener("click", () => void onLogin()));
  document.querySelectorAll<HTMLElement>("[data-action='logout']").forEach((element) => element.addEventListener("click", () => void logout().then(() => { render(); })));
  document.querySelectorAll<HTMLElement>("[data-action='refresh']").forEach((element) => element.addEventListener("click", () => void loadWorkspace().then(render).catch((error: unknown) => { statusMessage = error instanceof Error ? error.message : "更新できませんでした。"; render(); })));
  document.querySelectorAll<HTMLElement>("[data-action='start-action']").forEach((element) => element.addEventListener("click", () => void onStartAction(element.dataset.id ?? "")));
  document.querySelectorAll<HTMLElement>("[data-action='coming-soon']").forEach((element) => element.addEventListener("click", () => { statusMessage = "この画面は次のフェーズで追加されます。"; render(); }));
  document.querySelectorAll<HTMLElement>("[data-action='capture']").forEach((element) => element.addEventListener("click", () => { statusMessage = "取引 capture は API の準備ができています。Native capture UI と接続予定です。"; render(); }));
  document.querySelector<HTMLFormElement>("[data-form='business']")?.addEventListener("submit", (event) => { event.preventDefault(); void onBusinessSubmit(event.currentTarget as HTMLFormElement); });
  document.querySelector<HTMLSelectElement>("[data-action='select-business']")?.addEventListener("change", (event) => { const id = (event.target as HTMLSelectElement).value; selectedBusiness = businesses.find((business) => business.id === id) ?? null; void loadWorkspace().then(render); });
}

async function boot(): Promise<void> {
  try {
    await completeLogin();
    if (currentSession()) await loadWorkspace();
  } catch (error) {
    statusMessage = error instanceof Error ? error.message : "アプリを開始できませんでした。";
  }
  render();
}

if (isOAuthCallbackPath(window.location.pathname)) {
  mountOAuthCallbackBridge(appRoot);
} else {
  void boot();
}
